import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { apiClient, orgUiPath } from '@/core/api';
import { mintOrgSession } from '@/core/sessionAuth';
import { buildChooseOrgPath } from '@/core/urlRouting';
import { Spinner } from '@/components/primitives/Spinner';
import type { MeResponse } from '@/types/api';

function OrgRouteGuardInner({ orgId }: { orgId: string }) {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alternateOrgId, setAlternateOrgId] = useState<number | null>(null);

  const returnPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    let cancelled = false;

    apiClient<MeResponse>('/users/me')
      .then(async (me) => {
        if (cancelled) return;
        const allowed = me.organizations.some((o) => o.id != null && String(o.id) === orgId);
        if (!allowed) {
          const fallback = me.organizations.find((o) => o.id != null);
          setAlternateOrgId(fallback?.id ?? null);
          setForbidden(true);
          return;
        }
        await mintOrgSession(orgId);
        if (!cancelled) setReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to verify workspace access');
      });

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (forbidden) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface aspectly:px-6">
        <span className="material-symbols-outlined aspectly:text-error aspectly:text-5xl">block</span>
        <h1 className="aspectly:font-headline aspectly:text-xl aspectly:font-bold aspectly:text-on-surface">
          Workspace access denied
        </h1>
        <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:text-center aspectly:max-w-md">
          You do not have access to organization #{orgId}, or it does not exist. Choose a workspace
          you belong to, or sign in with a different account.
        </p>
        <div className="aspectly:flex aspectly:flex-wrap aspectly:items-center aspectly:justify-center aspectly:gap-3 aspectly:mt-2">
          <Link
            to={buildChooseOrgPath(returnPath)}
            className="aspectly:px-4 aspectly:py-2 aspectly:rounded-xl aspectly:bg-primary aspectly:text-on-primary aspectly:text-sm aspectly:font-bold aspectly:hover:opacity-90"
          >
            Choose workspace
          </Link>
          {alternateOrgId != null ? (
            <Link
              to={orgUiPath(alternateOrgId, '/')}
              className="aspectly:px-4 aspectly:py-2 aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/30 aspectly:text-sm aspectly:font-bold aspectly:text-on-surface aspectly:hover:bg-surface-container-low"
            >
              Go to {alternateOrgId === 786 ? 'platform' : 'my'} org
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface">
        <span className="material-symbols-outlined aspectly:text-error aspectly:text-4xl">warning</span>
        <p className="aspectly:text-on-surface-variant aspectly:text-sm">{error}</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  return <Outlet />;
}

/** Validates URL org membership and ensures a session cookie is minted. */
export function OrgRouteGuard() {
  const { orgId } = useParams<{ orgId: string }>();
  if (!orgId) return null;
  return <OrgRouteGuardInner key={orgId} orgId={orgId} />;
}
